import React from 'react';
import { motion } from 'framer-motion';
export function PlatformFlow() {
  return <section className="bg-sure-dark w-full py-24 md:py-32" data-id="element-143">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12" data-id="element-144">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="mb-12" data-id="element-145">
          <h2 className="text-sure-white text-2xl md:text-3xl font-bold mb-4" data-id="element-146">
            How it fits into your existing environment
          </h2>
          <p className="text-sure-gray text-base md:text-lg font-light leading-relaxed max-w-3xl" data-id="element-147">
            SURE AI integrates alongside existing onboard and operational
            systems, creating a continuous layer of intelligence without
            requiring infrastructure replacement.
          </p>
        </motion.div>

        {/* Flow Diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12 relative" data-id="element-148">
          {/* Left Column: Existing Systems */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} className="bg-[#1a1a1a] p-8 rounded-sm border border-white/10 relative" data-id="element-149">
            <h3 className="text-sure-white text-sm font-bold uppercase tracking-widest mb-6" data-id="element-150">
              Existing Systems
            </h3>
            <ul className="space-y-4 text-sure-gray text-sm font-light leading-relaxed mb-6" data-id="element-151">
              <li data-id="element-152">• Onboard CCTV systems</li>
              <li data-id="element-153">• Vehicle and telematics systems</li>
              <li data-id="element-154">• Existing transport IT and data environments</li>
            </ul>
            <p className="text-sure-accent text-xs uppercase tracking-widest font-medium" data-id="element-155">
              Deploys alongside existing systems. No disruption. No replacement.
            </p>

            {/* Arrow (Desktop) */}
            <div className="hidden lg:block absolute top-1/2 -right-6 -translate-y-1/2" data-id="element-156">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sure-accent" data-id="element-157">
                <polyline points="9 18 15 12 9 6" data-id="element-158"></polyline>
              </svg>
            </div>

            {/* Arrow (Mobile) */}
            <div className="lg:hidden flex justify-center mt-6" data-id="element-159">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sure-accent" data-id="element-160">
                <polyline points="6 9 12 15 18 9" data-id="element-161"></polyline>
              </svg>
            </div>
          </motion.div>

          {/* Center Column: SURE AI Layer */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="bg-sure-black p-8 rounded-sm border-2 border-sure-accent relative" data-id="element-162">
            <h3 className="text-sure-accent text-base font-bold uppercase tracking-widest mb-6" data-id="element-163">
              SURE AI Intelligence Layer
            </h3>
            <p className="text-sure-white text-sm font-medium mb-6" data-id="element-164">
              Transforms onboard signals into structured, anonymised operational
              intelligence
            </p>
            <ul className="space-y-4 text-sure-gray text-sm font-light leading-relaxed mb-6" data-id="element-165">
              <li className="border-l-2 border-sure-accent pl-4" data-id="element-166">
                <span className="text-sure-white font-medium" data-id="element-167">
                  Passenger movement and dwell insights
                </span>
                <br data-id="element-168" />
                Continuous visibility of boarding, alighting and stop-level
                behaviour
              </li>
              <li className="border-l-2 border-sure-accent pl-4" data-id="element-169">
                <span className="text-sure-white font-medium" data-id="element-170">
                  Operational performance signals
                </span>
                <br data-id="element-171" />
                Clear, comparable indicators across routes, services and
                locations
              </li>
              <li className="border-l-2 border-sure-accent pl-4" data-id="element-172">
                <span className="text-sure-white font-medium" data-id="element-173">
                  Safety and behavioural insights
                </span>
                <br data-id="element-174" />
                Identification of patterns and high-risk environments
              </li>
            </ul>
            <p className="text-sure-accent text-xs uppercase tracking-widest font-medium" data-id="element-175">
              Establishes consistent, network-wide visibility across operations
            </p>

            {/* Arrow (Desktop) */}
            <div className="hidden lg:block absolute top-1/2 -right-6 -translate-y-1/2" data-id="element-176">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sure-accent" data-id="element-177">
                <polyline points="9 18 15 12 9 6" data-id="element-178"></polyline>
              </svg>
            </div>

            {/* Arrow (Mobile) */}
            <div className="lg:hidden flex justify-center mt-6" data-id="element-179">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-sure-accent" data-id="element-180">
                <polyline points="6 9 12 15 18 9" data-id="element-181"></polyline>
              </svg>
            </div>
          </motion.div>

          {/* Right Column: Operational Environments */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="bg-[#1a1a1a] p-8 rounded-sm border border-white/10" data-id="element-182">
            <h3 className="text-sure-white text-sm font-bold uppercase tracking-widest mb-6" data-id="element-183">
              Operational Environments
            </h3>
            <ul className="space-y-4 text-sure-gray text-sm font-light leading-relaxed" data-id="element-184">
              <li data-id="element-185">• Operators and control centres</li>
              <li data-id="element-186">• Authorities and network planners</li>
              <li data-id="element-187">• Performance and service optimisation</li>
              <li data-id="element-188">• Governed, auditable decision-making</li>
            </ul>
          </motion.div>
        </div>
      </div>
    </section>;
}